import {
  REGISTER_USER_REQUEST,
  REGISTER_USER_FAILURE,
  REGISTER_USER_SUCCESS,
  GET_LICENSE_REQUEST,
  GET_LICENSE_SUCCESS,
  GET_LICENSE_FAILURE,
  CLEAR_INVITEE_LICENSES,
  VERIFY_EMAIL_UNIQUE_REQUEST,
  VERIFY_EMAIL_UNIQUE_SUCCESS,
  VERIFY_EMAIL_UNQIUE_FAILURE,
  VERIFY_USERNAME_UNIQUE_REQUEST,
  VERIFY_USERNAME_UNIQUE_SUCCESS,
  VERIFY_USERNAME_UNQIUE_FAILURE,
} from '@constants/actionTypes'
import { LOADING, SUCCEEDED, ERROR } from '@constants/fetchStatuses'

import 'isomorphic-fetch'

import processError from '../helpers/processError'

function requestRegistration(args) {
  return {
    type: REGISTER_USER_REQUEST,
    isFetching: true,
    status: LOADING,
    args,
  }
}

function receiveRegistration(message) {
  return {
    type: REGISTER_USER_SUCCESS,
    isFetching: false,
    status: SUCCEEDED,
    message,
  }
}

function registrationError(message) {
  return {
    type: REGISTER_USER_FAILURE,
    isFetching: false,
    status: ERROR,
    message,
  }
}

export function registerUser(args, signupRedesignEnabled = false) {
  const config = {
    method: 'POST',
    accept: 'application/json',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  }
  const url = `${SERVER_BASE_URL}/api/users/${signupRedesignEnabled ? 'invite' : ''}`
  return (dispatch) => {
    dispatch(requestRegistration(args))
    return fetch(url, config).then((response) => {
      return response
        .json()
        .then((data) => ({ data, response }))
        .then(({ data, response }) => {
          if (!response.ok) {
            dispatch(registrationError(processError(data)))
            return Promise.reject(data)
          } else {
            dispatch(receiveRegistration(data))
          }
        })
        .catch((err) => {
          console.log(err)
        })
    })
  }
}

export function signupUser(args, shouldCompleteRegistration = true) {
  const userType = args.user_type || 'default'
  const url = `${SERVER_BASE_URL}/api/users/signup/${userType}`
  const config = {
    method: 'POST',
    accept: 'application/json',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  }
  return (dispatch) => {
    dispatch(requestRegistration(args))
    return fetch(url, config).then((response) => {
      return response
        .json()
        .then((data) => ({ data, response }))
        .then(({ data, response }) => {
          if (!response.ok) {
            dispatch(registrationError(processError(data)))
            return Promise.reject(data)
          } else {
            // Only complete registration flow if explicitly requested
            if (shouldCompleteRegistration) {
              dispatch(receiveRegistration(data, args, true))
            }
            // Always return the data for chaining
            return { data, args }
          }
        })
        .catch((err) => {
          console.log(err)
        })
    })
  }
}

// New function to manually complete registration after chained operations
export function completeRegistration(data, args) {
  return (dispatch) => {
    return dispatch(receiveRegistration(data, args, true))
  }
}

function getLicenseRequest() {
  return {
    type: GET_LICENSE_REQUEST,
    isGetting: true,
  }
}

function getLicenseSuccess(license, licenses) {
  return {
    type: GET_LICENSE_SUCCESS,
    isGetting: false,
    license,
    licenses,
  }
}

function getLicenseFailure(message) {
  return {
    type: GET_LICENSE_FAILURE,
    isGetting: false,
    message,
  }
}

export function clearInviteeLicenses() {
  return {
    type: CLEAR_INVITEE_LICENSES,
    inviteeLicenses: {},
  }
}

export function getLicenses(id, state) {
  const url = `${SERVER_BASE_URL}/api/licenses/${state}/${id}`
  const config = {
    method: 'GET',
    accept: 'application/json',
  }
  return (dispatch, getState) => {
    dispatch(getLicenseRequest())

    // If we already have this license in the state, then just switch it out in the license spot
    const licenses = getState().registration.licenses
    const existingLicense = licenses.find((l) => l.license_number === id && l.state === state)

    if (existingLicense) {
      dispatch(getLicenseSuccess(existingLicense, licenses))
    } else {
      return fetch(url, config).then((response) => {
        return response
          .json()
          .then((appraiserLicenses) => ({ appraiserLicenses, response }))
          .then(({ appraiserLicenses, response }) => {
            if (!response.ok) {
              dispatch(getLicenseFailure(processError(appraiserLicenses)))
              return Promise.reject(appraiserLicenses)
            } else {
              dispatch(getLicenseSuccess(null, appraiserLicenses))
            }
          })
          .catch((err) => console.log(err))
      })
    }
  }
}

function verifyEmailUniqueRequest() {
  return {
    type: VERIFY_EMAIL_UNIQUE_REQUEST,
    isVerifyingEmail: true,
  }
}

function verifyEmailUniqueSuccess(license) {
  return {
    type: VERIFY_EMAIL_UNIQUE_SUCCESS,
    isVerifyingEmail: false,
  }
}

function verifyEmailUniqueFailure(message) {
  return {
    type: VERIFY_EMAIL_UNQIUE_FAILURE,
    isVerifyingEmail: false,
    message,
  }
}

export function verifyEmailUnique(email) {
  const url = `${SERVER_BASE_URL}/api/users/unique/email/${email}`
  const config = {
    method: 'GET',
    accept: 'application/json',
  }

  return (dispatch, getState) => {
    dispatch(verifyEmailUniqueRequest())
    return fetch(url, config).then((response) => {
      return response
        .json()
        .then((result) => ({ result, response }))
        .then(({ result, response }) => {
          if (!response.ok) {
            dispatch(verifyEmailUniqueFailure(processError(result)))
            return Promise.reject(result)
          } else {
            dispatch(verifyEmailUniqueSuccess(result))
          }
        })
        .catch((err) => console.log(err))
    })
  }
}

function verifyUsernameUniqueRequest() {
  return {
    type: VERIFY_USERNAME_UNIQUE_REQUEST,
    isVerifyingUsername: true,
  }
}

function verifyUsernameUniqueSuccess(license) {
  return {
    type: VERIFY_USERNAME_UNIQUE_SUCCESS,
    isVerifyingUsername: false,
  }
}

function verifyUsernameUniqueFailure(message) {
  return {
    type: VERIFY_USERNAME_UNQIUE_FAILURE,
    isVerifyingUsername: false,
    message,
  }
}

export function verifyUsernameUnique(username) {
  const url = `${SERVER_BASE_URL}/api/users/unique/username/${username}`
  const config = {
    method: 'GET',
    accept: 'application/json',
  }

  return (dispatch, getState) => {
    dispatch(verifyUsernameUniqueRequest())
    return fetch(url, config).then((response) => {
      return response
        .json()
        .then((result) => ({ result, response }))
        .then(({ result, response }) => {
          if (!response.ok) {
            dispatch(verifyUsernameUniqueFailure(processError(result)))
            return Promise.reject(result)
          } else {
            dispatch(verifyUsernameUniqueSuccess(result))
          }
        })
        .catch((err) => console.log(err))
    })
  }
}
